<template>
  <div data-test="eventDetails">
    <template v-if="$vuetify.display.mdAndUp">
      <EventCardLargeDesktop
        v-bind="{ state, event }"
        @click:settings="() => (settings = true)"
      >
        <template #icon-links>
          <slot name="icon-links" />
        </template>
        <template #event-additional-details>
          <slot name="event-additional-details" />
        </template>
      </EventCardLargeDesktop>
    </template>
    <template v-else>
      <EventCardLargeMobile
        v-bind="{ state, event }"
        @click:settings="() => (settings = true)"
      >
        <template #icon-links>
          <slot name="icon-links" />
        </template>
        <template #event-additional-details>
          <slot name="event-additional-details" />
        </template>
      </EventCardLargeMobile>
    </template>

    <EventManagementDialog
      v-model="settings"
      :event="event"
      @refetch="$emit('refetch')"
    />
  </div>
</template>
<script>
import { defineComponent, ref } from "vue";
import gql from "fraql";
import { uniq, get } from "lodash-es";
import { getFederationConfiguration } from "@composables/masterdata/config";
import { useDisplayText } from "@composables/display_text";

import EventCardLargeDesktop from "./EventCardLargeDesktop.vue";
import EventCardLargeMobile from "./EventCardLargeMobile.vue";

import AppShowEventCupComponent from "@/components/elements/AppShows/event/AppShowEventCupComponent.vue";
import AppShowDisciplineName from "@/components/elements/AppShows/AppShowDisciplineName.vue";

import AppShowRegistrationBtn from "@/components/elements/AppShows/event/AppShowRegistrationBtn.vue";
import AppEquipeBtn from "@/components/elements/AppEquipeBtn.vue";
import AppShowEventReferenceCode from "@AppShows/event/AppShowEventReferenceCode.vue";

import AppShowRegistrationDeadline from "@AppShows/event/AppShowRegistrationDeadline.vue";

import EventManagementDialog from "../EventManagementDialog.vue";

export default defineComponent({
  name: "EventCardLarge",
  components: {
    EventCardLargeDesktop,
    EventCardLargeMobile,
    EventManagementDialog
  },
  inject: {
    isNoncompetition: {
      default: false
    }
  },
  props: {
    event: {
      type: Object,
      required: true
    }
  },
  emits: ["refetch"],
  setup() {
    const { displayText } = useDisplayText();
    const settings = ref(false);

    return {
      ...getFederationConfiguration(),
      displayText,
      settings
    };
  },
  computed: {
    eventCompetitions() {
      if (this.event.status == "results_published") {
        return this.event.competitions.filter(
          c => c.forRegistrationOnly == false
        );
      }
      return this.event.competitions;
    },
    state() {
      return Object.defineProperties(
        {},
        {
          eventCompetitions: {
            get: () => {
              return this.eventCompetitions;
            }
          },
          canUsePayments: {
            get: () => {
              return (
                this.event.useOnlinePayments &&
                this.$allowedMutation(this.event.allowedFields, [
                  "financialSummary"
                ])
              );
            }
          },
          canUseEquipe: {
            get: () => {
              return (
                this.event.useEquipe &&
                this.$allowedMutation(this.event.allowedMutations, [
                  "noncompetitionEventCreateOrUpdate",
                  "eventCreateOrUpdate",
                  "entryUpdateResult",
                  "eventCompetitionBatchUpdateResults"
                ])
              );
            }
          },
          equipeDisabled: {
            get: () => {
              return [
                "declined",
                "schedule_declined",
                "registration_open",
                "",
                "unknown",
                "discarded",
                null
              ].includes(this.event.status);
            }
          },
          canViewProductsMasterlist: {
            get: () => {
              return (
                this.$allowedMutation(
                  this.event.allowedFields,
                  "userCanAccessProductsMasterlist"
                ) &&
                this.event.eventProducts.some(x =>
                  [
                    "fully_during_competition_free_booking",
                    "booking_online_rest_during_competition",
                    "full_price_online_booking_nonrefundable",
                    "full_price_online_no_refund"
                  ].includes(x.paymentStructure)
                )
              );
            }
          },
          canViewReports: {
            get: () => {
              return this.$allowedMutation(this.event.allowedFields, [
                "eventSurveys"
              ]);
            }
          },
          canEditEvent: {
            get: () => {
              return this.$allowedMutation(this.event.allowedMutations, [
                "noncompetitionEventCreateOrUpdate",
                "eventCreateOrUpdate",
                "eventPropose",
                "eventPublish",
                "eventDecline",
                "eventProposeSchedule",
                "eventPublishSchedule",
                "noncompetitionEventOpenRegistration",
                "eventDeclineSchedule",
                "eventClose"
              ]);
            }
          },
          canEnterMasterlist: {
            get: () => {
              return this.$allowedMutation(
                this.event.allowedFields,
                "userCanAccessMasterlist"
              );
            }
          },
          canEnterDashboard: {
            get: () => {
              return (
                this.$allowedMutation(this.event.allowedMutations, [
                  "eventCreateOrUpdate",
                  "eventCompetitionCreateOrUpdate",
                  "noncompetitionEventCreateOrUpdate"
                ]) ||
                this.$allowedMutation(
                  this.event.allowedFields,
                  "userCanAccessDashboard"
                )
              );
            }
          },
          regionName: {
            get: () => {
              return get(this.event, "region.name", "");
            }
          },
          RegistrationDeadline: {
            get: () => {
              if (this.event.registrationEndDate)
                return this.$dayjs.date(this.event.registrationEndDate);
              else return "";
            }
          },
          eventcodeNames: {
            get: () => {
              if (this.event.competitions) {
                const eventcodes = this.event.competitions.filter(
                  c => c.eventcode
                );
                return uniq(eventcodes).join(" | ");
              } else {
                return "";
              }
            }
          },
          eventDate: {
            get: () => {
              return this.$dayjs.nicerDate(
                this.event.startDate,
                this.event.endDate
              );
            }
          }
        }
      );
    }
  },
  fragments: {
    eventFields: gql`
      fragment _ on Event {
        useOnlinePayments
        status
        allowedFields
        allowedMutations
        hostOrganisation {
          id
          name
        }
        ${EventManagementDialog.fragments.eventFields}
        disciplineId
        discipline @client {
          ${AppShowDisciplineName.fragments.discipline}
        }
        ${AppShowRegistrationBtn.fragments.eventWithMutations}
        ${AppShowEventReferenceCode.fragments.eventFields}
        ${AppShowRegistrationDeadline.fragments.eventFields}
        competitions {
          id
          ${AppShowRegistrationDeadline.fragments.competitionFields}
          forRegistrationOnly
          ${EventManagementDialog.fragments.competitionFields}
        }
      }
    `,
    cupSeason: AppShowEventCupComponent.fragments.cupSeason,
    cups: AppShowEventCupComponent.fragments.cups,
    equipeAuthData: AppEquipeBtn.fragments.equipeAuthData
  }
});
</script>
